// Data transformation functions to convert real survey data to PulseAnalyticsV2 format

// Transform real survey questions to prototype format
export const transformQuestionsData = (questions, responses, participants) => {
  const surveyQuestions = Array.isArray(questions)
    ? questions.filter((question) => question?.question || question?.text)
    : [];

  let questionTemplates = surveyQuestions;
  let useResponseTaskList = false;

  if (responses?.length) {
    const firstResponse = responses[0];
    if (firstResponse?.task_response?.task_list) {
      questionTemplates = firstResponse.task_response.task_list;
      useResponseTaskList = true;
    }
  }

  if (!questionTemplates.length) return [];

  return questionTemplates.map((questionTemplate, index) => {
    // Fall back to the survey definition so analytics can render even before any responses exist.
    const questionResponses = (responses || [])
      .map((response) => {
        const taskResponse = response?.task_response?.task_list?.[index];
        if (!taskResponse) return null;

        return {
          employeeId: response.employee || response.task_response?.employee_id,
          response: taskResponse.response,
          followUp: taskResponse.follow_up
        };
      })
      .filter(Boolean);

    // Determine question type based on response patterns
    const questionType = determineQuestionType(questionTemplate, questionResponses);
    
    // Process responses based on question type
    const processedResponses = processResponsesByType(questionType, questionResponses);

    // Detect scale range for quantitative questions
    const scaleRange = questionType === 'quantitative' ? detectScaleRange(questionTemplate.question, questionResponses) : null;
    
    const questionId = useResponseTaskList
      ? `q${index + 1}`
      : questionTemplate.id || `q${index + 1}`;

    return {
      id: questionId,
      text: questionTemplate.question || questionTemplate.text || `Question ${index + 1}`,
      type: questionType,
      category: getDefaultCategory(questionType),
      responses: processedResponses,
      scaleRange: scaleRange
    };
  });
};

// Transform real survey participants/respondents to prototype format
export const transformRespondentsData = (participants, responses, questions) => {
  if (!responses || !responses.length) return [];

  // Extract questions from first response to get question count
  const firstResponse = responses[0];
  if (!firstResponse?.task_response?.task_list) return [];
  
  const questionCount = firstResponse.task_response.task_list.length;

  return responses.map((responseObj) => {
    const taskResponse = responseObj.task_response;
    if (!taskResponse?.task_list) return null;

    // Map responses by question, including follow-ups
    const responsesByQuestion = {};
    taskResponse.task_list.forEach((taskItem, index) => {
      const questionId = `q${index + 1}`;
      responsesByQuestion[questionId] = {
        response: taskItem.response,
        followUp: taskItem.follow_up || null
      };
    });

    return {
      id: responseObj.employee || taskResponse.employee_id,
      name: responseObj.employee_full_name || "Unknown Employee",
      department: "Unknown Department", // This would need to come from participants data if available
      responses: responsesByQuestion
    };
  }).filter(Boolean);
};

// Determine question type from question data and responses
const determineQuestionType = (question, responses) => {
  // Check if question has type metadata
  if (question.type) {
    // Map existing types to prototype types
    const typeMapping = {
      'scale': 'quantitative',
      'single': 'multiselect',
      'multi': 'multiselect',
      'text': 'openended'
    };
    return typeMapping[question.type] || 'openended';
  }

  // Analyze responses to determine type
  if (responses.length === 0) return 'openended';

  // Check if all responses are numeric (quantitative)
  const numericResponses = responses.filter(r => 
    !isNaN(parseInt(r.response)) && isFinite(parseInt(r.response))
  );
  
  if (numericResponses.length > responses.length * 0.8) {
    return 'quantitative';
  }

  // Check if responses have limited unique values (multiselect)
  const uniqueResponses = [...new Set(responses.map(r => r.response))];
  if (uniqueResponses.length <= 10 && responses.length > uniqueResponses.length * 2) {
    return 'multiselect';
  }

  // Default to open-ended
  return 'openended';
};

// Process responses based on question type
const processResponsesByType = (type, responses) => {
  switch (type) {
    case 'quantitative':
      return processQuantitativeResponses(responses);
    case 'multiselect':
      return processMultiselectResponses(responses);
    case 'openended':
      return processOpenEndedResponses(responses);
    default:
      return processOpenEndedResponses(responses);
  }
};

// Process quantitative/scale responses
const processQuantitativeResponses = (responses) => {
  const numericResponses = responses
    .map(r => parseInt(r.response))
    .filter(val => !isNaN(val) && isFinite(val));

  if (numericResponses.length === 0) {
    return { distribution: {}, average: 0, total: 0 };
  }

  // Create distribution
  const distribution = {};
  numericResponses.forEach(value => {
    distribution[value] = (distribution[value] || 0) + 1;
  });

  // Calculate average
  const sum = numericResponses.reduce((acc, val) => acc + val, 0);
  const average = (sum / numericResponses.length).toFixed(2);

  const result = {
    distribution,
    average: parseFloat(average),
    total: numericResponses.length
  };

  return result;
};

// Process multiselect responses
const processMultiselectResponses = (responses) => {
  const responseCounts = {};
  
  responses.forEach(r => {
    const response = r.response;
    responseCounts[response] = (responseCounts[response] || 0) + 1;
  });

  const options = Object.entries(responseCounts).map(([label, count]) => ({
    label,
    count,
    percentage: ((count / responses.length) * 100).toFixed(1)
  }));

  return {
    options,
    total: responses.length
  };
};

// Process open-ended responses
const processOpenEndedResponses = (responses) => {
  // Simple theme extraction based on common words/patterns
  const themes = extractThemes(responses.map(r => r.response));
  
  return {
    themes: themes.map(theme => ({
      theme: theme.name,
      count: theme.count,
      percentage: ((theme.count / responses.length) * 100).toFixed(1)
    })),
    total: responses.length
  };
};

// Basic theme extraction for open-ended responses
const extractThemes = (responses) => {
  if (responses.length === 0) return [];

  // Simple keyword-based theme extraction
  const commonKeywords = {
    'communication': ['communication', 'communicate', 'talk', 'discuss', 'meeting'],
    'work-life balance': ['balance', 'flexible', 'hours', 'time', 'remote'],
    'management': ['manager', 'management', 'supervisor', 'leadership', 'boss'],
    'career growth': ['growth', 'development', 'promotion', 'career', 'advancement'],
    'compensation': ['salary', 'pay', 'compensation', 'benefits', 'money'],
    'workplace environment': ['office', 'environment', 'space', 'culture', 'atmosphere']
  };

  const themeCounts = {};
  
  responses.forEach(response => {
    if (!response) return;
    
    const lowerResponse = response.toLowerCase();
    
    Object.entries(commonKeywords).forEach(([theme, keywords]) => {
      const hasKeyword = keywords.some(keyword => lowerResponse.includes(keyword));
      if (hasKeyword) {
        themeCounts[theme] = (themeCounts[theme] || 0) + 1;
      }
    });
  });

  // Convert to array and sort by count
  return Object.entries(themeCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5); // Top 5 themes
};

// Get default category based on question type
const getDefaultCategory = (type) => {
  const categoryMap = {
    'quantitative': 'Rating Scale Question',
    'multiselect': 'Multiple Choice Question',
    'openended': 'Open Text Question'
  };
  return categoryMap[type] || 'General Question';
};

// Helper functions for data processing (maintaining compatibility with existing mockData functions)
export const processChartData = (question) => {
  if (question.type === 'quantitative' && question.responses.distribution) {
    return Object.entries(question.responses.distribution).map(([score, count]) => ({
      score: parseInt(score),
      count,
      percentage: ((count / question.responses.total) * 100).toFixed(1)
    }));
  }
  return [];
};

export const getQuestionTypeLabel = (type) => {
  const typeLabels = {
    quantitative: "Likert",
    multiselect: "Category", 
    openended: "OpenEnded"
  };
  return typeLabels[type] || type;
};

// Detect scale range from question text and responses
const detectScaleRange = (questionText, responses) => {
  // First try to extract range from question text
  const rangePatterns = [
    /(\d+)\s*-\s*(\d+)/,           // "1-10", "0-5", etc.
    /(\d+)\s*to\s*(\d+)/i,        // "1 to 10"
    /scale\s*of\s*(\d+)\s*to\s*(\d+)/i,  // "scale of 1 to 10"
    /rate.*?(\d+)\s*to\s*(\d+)/i,        // "rate from 1 to 10"
  ];

  for (const pattern of rangePatterns) {
    const match = questionText.match(pattern);
    if (match) {
      return {
        min: parseInt(match[1]),
        max: parseInt(match[2])
      };
    }
  }

  // If no pattern found in text, analyze actual response values
  if (responses && responses.length > 0) {
    const numericResponses = responses
      .map(r => parseInt(r.response))
      .filter(val => !isNaN(val) && isFinite(val));
    
    if (numericResponses.length > 0) {
      const min = Math.min(...numericResponses);
      const max = Math.max(...numericResponses);
      
      // Common scale patterns
      if (min >= 1 && max <= 5) return { min: 1, max: 5 };
      if (min >= 1 && max <= 10) return { min: 1, max: 10 };
      if (min >= 0 && max <= 10) return { min: 0, max: 10 };
      if (min >= 0 && max <= 5) return { min: 0, max: 5 };
      
      // Default to observed range
      return { min, max };
    }
  }

  // Default fallback
  return { min: 1, max: 5 };
};

export const formatResponseCount = (count) => {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`;
  }
  return count.toString();
};